import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import SuperadminLayout, { DarkCard } from "./SuperadminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MagnifyingGlass, PencilSimple, ClockCounterClockwise, ArrowSquareOut, DownloadSimple } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SuperadminOrganizations() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type_, setType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [historyOrg, setHistoryOrg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (type_ !== "all") params.type = type_;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/superadmin/organizations", { params });
      setItems(data.items);
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [q, type_, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const save = async (organization_id, changes, reason) => {
    try {
      await api.patch(`/superadmin/organizations/${organization_id}`, { ...changes, reason });
      toast.success("Organisation mise à jour");
      setEditOrg(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  return (
    <SuperadminLayout title="Organisations">
      <DarkCard className="p-4 mb-6" data-testid="orgs-filters">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8494A8]" />
            <Input placeholder="Nom organisation…" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]" data-testid="orgs-search" />
          </div>
          <Select value={type_} onValueChange={setType}>
            <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="orgs-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="solo">Solo (cabinet individuel)</SelectItem>
              <SelectItem value="establishment">Établissement</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-[#081221] border-white/10 text-white" data-testid="orgs-status-filter"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="active">Actives</SelectItem>
              <SelectItem value="suspended">Suspendues</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </DarkCard>

      <DarkCard className="overflow-hidden" data-testid="orgs-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[#8494A8]">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Organisation</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Sièges</th>
              <th className="text-left px-4 py-3 font-medium">Membres</th>
              <th className="text-left px-4 py-3 font-medium">Plan</th>
              <th className="text-left px-4 py-3 font-medium">Statut</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-[#8494A8]">Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#8494A8]">Aucune organisation</td></tr>}
            {items.map((o) => (
              <tr key={o.organization_id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`org-row-${o.organization_id}`}>
                <td className="px-4 py-3">
                  <p className="text-white font-medium">{o.name || "—"}</p>
                  <p className="text-[#8494A8] text-xs font-mono">{o.organization_id.slice(0, 16)}</p>
                </td>
                <td className="px-4 py-3">
                  <Badge className={o.type === "establishment" ? "bg-[#3A8FA5]/25 text-[#7BC3D4] hover:bg-[#3A8FA5]/25" : "bg-white/10 text-white/70 hover:bg-white/10"}>
                    {o.type || "solo"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">
                  {o.seats_used || 0} / {o.seats_paid || 1}
                </td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">{o.members_count || 0}</td>
                <td className="px-4 py-3 text-[#B7C4D6] text-xs">{o.plan || "—"}</td>
                <td className="px-4 py-3">
                  {o.is_suspended ? (
                    <Badge className="bg-red-500/25 text-red-300 hover:bg-red-500/25">Suspendu</Badge>
                  ) : (
                    <Badge className="bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20">Actif</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                  <Button variant="ghost" size="sm"
                    className="text-[#B7C4D6] hover:bg-white/10 hover:text-white"
                    onClick={() => setHistoryOrg(o)} data-testid={`org-history-${o.organization_id}`}>
                    <ClockCounterClockwise size={14} className="mr-1" /> Historique
                  </Button>
                  <Button variant="ghost" size="sm"
                    className="text-[#B7C4D6] hover:bg-white/10 hover:text-white"
                    onClick={() => setEditOrg(o)} data-testid={`org-edit-${o.organization_id}`}>
                    <PencilSimple size={14} className="mr-1" /> Modifier
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DarkCard>

      {editOrg && <EditOrgDialog org={editOrg} onSave={save} onClose={() => setEditOrg(null)} />}
      {historyOrg && <SeatHistoryDialog org={historyOrg} onClose={() => setHistoryOrg(null)} />}
    </SuperadminLayout>
  );
}

function SeatHistoryDialog({ org, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/superadmin/organizations/${org.organization_id}/seat-history`);
        setData(data);
      } catch (e) { toast.error("Historique indisponible"); }
      finally { setLoading(false); }
    })();
  }, [org.organization_id]);

  const fmt = (cents, ccy) => {
    if (cents == null) return null;
    return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: (ccy || "eur").toUpperCase() });
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/superadmin/organizations/${org.organization_id}/seat-history`, {
        params: { format: "csv", limit: 200 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = resp.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `questiomed-sieges-${org.organization_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Historique CSV téléchargé");
    } catch (e) { toast.error("Export CSV impossible"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="seat-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <ClockCounterClockwise size={22} weight="duotone" className="text-[#3A8FA5]" />
            Historique des sièges — {org.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto -mx-6 px-6">
          {loading && <p className="text-sm text-[#8494A8] py-8 text-center">Chargement…</p>}
          {!loading && (!data || data.items.length === 0) && (
            <p className="text-sm text-[#8494A8] py-8 text-center italic">Aucun changement enregistré</p>
          )}
          {!loading && data && data.items.length > 0 && (
            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-white/10" />
              {data.items.map((row, i) => {
                const changes = row.extra?.changes || {};
                const seatsPaidChanged = changes.seats_paid !== undefined;
                const seatsFreeChanged = changes.seats_free !== undefined;
                return (
                  <div key={i} className="relative pb-6 last:pb-2" data-testid={`history-row-${i}`}>
                    <span className="absolute -left-[19px] top-1.5 w-3 h-3 rounded-full bg-[#E4572E] border-2 border-[#0F2136]" />
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs text-[#8494A8]">
                        {new Date(row.at).toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                      <p className="text-[10px] text-[#8494A8] font-mono">{row.user_email}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {seatsPaidChanged && (
                        <Badge className="bg-[#E4572E]/25 text-[#FFB59E] hover:bg-[#E4572E]/25 border border-[#E4572E]/30">
                          Sièges payants → {changes.seats_paid}
                        </Badge>
                      )}
                      {seatsFreeChanged && (
                        <Badge className="bg-[#B0C97F]/20 text-[#D4E5AB] hover:bg-[#B0C97F]/20 border border-[#B0C97F]/30">
                          Sièges gratuits → {changes.seats_free}
                        </Badge>
                      )}
                      {changes.is_suspended !== undefined && (
                        <Badge className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/20">
                          {changes.is_suspended ? "Suspendu" : "Réactivé"}
                        </Badge>
                      )}
                    </div>
                    {row.extra?.reason && (
                      <p className="text-sm text-[#B7C4D6] italic mb-2">« {row.extra.reason} »</p>
                    )}
                    {row.invoice && (
                      <div className="mt-2 p-3 bg-[#081221] rounded-xl border border-white/10 flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#8494A8] font-mono">Facture Stripe {row.invoice.number || row.invoice.id}</p>
                          <p className="text-sm text-white font-mono mt-0.5">
                            {fmt(row.invoice.amount_paid || row.invoice.amount_due, row.invoice.currency)}
                            <Badge className={`ml-2 text-xs ${row.invoice.status === "paid" ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20" : "bg-amber-500/20 text-amber-300 hover:bg-amber-500/20"}`}>
                              {row.invoice.status}
                            </Badge>
                          </p>
                        </div>
                        {row.invoice.hosted_invoice_url && (
                          <a href={row.invoice.hosted_invoice_url} target="_blank" rel="noreferrer"
                             className="text-[#7BC3D4] hover:text-white text-xs inline-flex items-center gap-1 whitespace-nowrap">
                            Ouvrir <ArrowSquareOut size={12} />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter className="sm:justify-between flex-row items-center">
          <Button
            variant="outline"
            onClick={downloadCsv}
            disabled={loading || !data || data.items.length === 0}
            className="border-white/20 text-white hover:bg-white/10 disabled:opacity-40"
            data-testid="seat-history-csv-btn"
          >
            <DownloadSimple size={16} className="mr-2" /> Exporter CSV
          </Button>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditOrgDialog({ org, onSave, onClose }) {
  const isEstab = (org.type || "solo") === "establishment";
  const [seatsPaid, setSeatsPaid] = useState(org.seats_paid || 1);
  const [seatsFree, setSeatsFree] = useState(org.seats_free || 0);
  const [suspended, setSuspended] = useState(!!org.is_suspended);
  const [name, setName] = useState(org.name || "");
  const [reason, setReason] = useState("");
  const [prorate, setProrate] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const seatsPaidChanged = isEstab && seatsPaid !== (org.seats_paid || 1);

  const fetchPreview = async () => {
    if (!seatsPaidChanged) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/organizations/${org.organization_id}/seat-preview`, { seats_paid: seatsPaid });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Prévisualisation impossible"); }
    finally { setBusy(false); }
  };

  const doSave = () => {
    const payload = {
      name,
      seats_paid: isEstab ? seatsPaid : 1,
      seats_free: isEstab ? seatsFree : 0,
      is_suspended: suspended,
      stripe_prorate: prorate,
    };
    onSave(org.organization_id, payload, reason);
  };

  const fmt = (cents, ccy) => {
    if (cents == null) return "—";
    return (cents / 100).toLocaleString("fr-FR", { style: "currency", currency: (ccy || "eur").toUpperCase() });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F2136] border-white/10 text-white max-w-lg" data-testid="edit-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {org.name}
            <Badge className={isEstab ? "bg-[#3A8FA5]/25 text-[#7BC3D4] hover:bg-[#3A8FA5]/25" : "bg-white/10 text-white/70 hover:bg-white/10"}>
              {isEstab ? "Établissement" : "Solo"}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 bg-[#081221] border-white/10 text-white" data-testid="edit-org-name" />
          </div>
          {isEstab ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Sièges payants (Stripe)</Label>
                  <Input type="number" min={1} max={200} value={seatsPaid}
                    onChange={(e) => { setSeatsPaid(parseInt(e.target.value) || 1); setPreview(null); }}
                    className="mt-2 bg-[#081221] border-white/10 text-white" data-testid="edit-org-seats-paid" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Sièges gratuits (offre)</Label>
                  <Input type="number" min={0} max={200} value={seatsFree}
                    onChange={(e) => setSeatsFree(parseInt(e.target.value) || 0)}
                    className="mt-2 bg-[#081221] border-white/10 text-white" data-testid="edit-org-seats-free" />
                </div>
              </div>
              <p className="text-xs text-[#8494A8]">Total : {seatsPaid + seatsFree} sièges. Les sièges gratuits n'impactent pas Stripe (offre commerciale).</p>
              {seatsPaidChanged && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                  <p className="text-xs text-[#F5D68A] font-semibold mb-2">Changement Stripe imminent</p>
                  <label className="flex items-center gap-2 text-xs text-[#B7C4D6] cursor-pointer mb-2">
                    <input type="checkbox" checked={prorate} onChange={(e) => setProrate(e.target.checked)} className="accent-[#E4572E]" data-testid="edit-org-prorate" />
                    Créer une proration immédiate (facturer/créditer au prorata)
                  </label>
                  <Button size="sm" onClick={fetchPreview} disabled={busy}
                    className="bg-white/10 hover:bg-white/20 text-white w-full" data-testid="edit-org-preview-btn">
                    {busy ? "Calcul…" : "Prévisualiser le montant facturé"}
                  </Button>
                  {preview && (
                    <div className="mt-3 text-xs text-[#B7C4D6] space-y-1">
                      <div className="flex justify-between"><span>Sièges actuels :</span><span className="font-mono text-white">{preview.current_seats}</span></div>
                      <div className="flex justify-between"><span>Sièges cible :</span><span className="font-mono text-white">{preview.target_seats}</span></div>
                      <div className="flex justify-between border-t border-white/10 pt-1 mt-1">
                        <span>Facturation immédiate estimée :</span>
                        <span className={`font-mono font-bold ${preview.proration_amount > 0 ? "text-amber-300" : "text-emerald-300"}`}>
                          {fmt(preview.proration_amount, preview.currency)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="p-3 bg-white/[0.03] rounded-xl border border-white/10 text-xs text-[#8494A8]">
              Abonnement solo — nombre de sièges verrouillé à <strong className="text-white">1</strong>. Pour ajouter des praticiens, passez le compte à Établissement.
            </div>
          )}
          <div className="flex items-center gap-3">
            <input id="suspended" type="checkbox" checked={suspended} onChange={(e) => setSuspended(e.target.checked)}
              className="w-4 h-4 accent-[#E4572E]" data-testid="edit-org-suspended" />
            <Label htmlFor="suspended" className="text-sm text-[#B7C4D6]">Suspendre l'organisation</Label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8494A8]">Motif</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-2 bg-[#081221] border-white/10 text-white placeholder:text-[#5F758F]"
              placeholder="Ex: geste commercial, upgrade demandé…" data-testid="edit-org-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#B7C4D6] hover:bg-white/10 hover:text-white">Annuler</Button>
          <Button onClick={doSave}
            className="bg-[#E4572E] hover:bg-[#F27551] text-white" data-testid="edit-org-save">
            {seatsPaidChanged && prorate ? "Confirmer et prélever Stripe" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
